
import { defineComponent } from 'vue';
import {
  Matomo,
  ContentBlock,
  ContentTable,
  SiteRef,
  Notification,
  MatomoUrl,
  translate,
  Site,
  externalRawLink,
  Tooltips,
} from 'CoreHome';
import { Field } from 'CorePluginsAdmin';
import { getDisplayApiKey } from '../utilities';
import SearchEngineNavigation from '../Navigation/Navigation.vue';
import WebsitesAvailableModal from '../components/WebsitesAvailableModal.vue';
import CredentialsErrorBlock from '../components/CredentialsErrorBlock.vue';

interface YandexConfigState {
  removeAccountConfigName: string;
  removeAccountName: string;
  isAddingMeasurable: boolean;
  currentSiteToAdd: SiteRef;
  yandexAccountAndHostIdToAdd: string|null;
  clientIdToUse: string;
  clientSecretToUse: string;
}

interface YandexAccount {
  name: string;
  apiKey: string;
  urls: Record<string, { host_id: string; verified: boolean }>;
}

interface ConfiguredYandexInfo {
  yandexAccountAndHostId: string;
}

export default defineComponent({
  props: {
    isClientConfigured: Boolean,
    isClientConfigurable: Boolean,
    isOAuthConfigured: Boolean,
    clientId: String,
    clientSecret: String,
    configuredMeasurables: {
      type: Object,
      required: true,
    },
    sitesInfos: {
      type: Object,
      required: true,
    },
    providerName: {
      type: String,
      default: '',
    },
    currentSite: {
      type: Object,
      required: true,
    },
    urlOptions: {
      type: [Object, Array],
      required: true,
    },
    hasOAuthError: [String, Boolean],
    accounts: {
      type: Object,
      required: true,
    },
    auth_nonce: {
      type: String,
      required: true,
    },
    formNonce: {
      type: String,
      required: true,
    },
    addYandexSiteConfigNonce: {
      type: String,
      required: true,
    },
    removeYandexSiteConfigNonce: {
      type: String,
      required: true,
    },
    removeYandexAccountNonce: {
      type: String,
      required: true,
    },
    countOfAccountsWithAccess: Number,
    userIsSuperUser: String,
    baseDomain: String,
    baseDomainUrl: String,
    hasCredentialsError: Boolean,
    resetCredentialsUrl: String,
    removeClientConfigUrl: String,
  },
  components: {
    SearchEngineNavigation,
    ContentBlock,
    CredentialsErrorBlock,
    Field,
    Notification,
    WebsitesAvailableModal,
  },
  directives: {
    ContentTable,
    Tooltips,
  },
  data(): YandexConfigState {
    return {
      removeAccountConfigName: '',
      removeAccountName: '',
      isAddingMeasurable: false,
      currentSiteToAdd: this.currentSite as SiteRef,
      yandexAccountAndHostIdToAdd: null,
      clientIdToUse: '',
      clientSecretToUse: '',
    };
  },
  methods: {
    accountHasAvailableSites(account: YandexAccount) {
      return Object.values(account.urls).some(
        (siteAccess) => siteAccess.verified,
      );
    },
    getAvailableWebsites(account: YandexAccount): string[] {
      return Object.entries(account.urls)
        .filter(([, siteAccess]) => siteAccess.verified)
        .map(([url]) => url.replaceAll('sc-domain:', ''))
        .sort((first, second) => first.localeCompare(second, undefined, { sensitivity: 'base' }));
    },
    getUnverifiedWebsites(account: YandexAccount): string[] {
      return Object.entries(account.urls)
        .filter(([, siteAccess]) => !siteAccess.verified)
        .map(([url]) => url.replaceAll('sc-domain:', ''))
        .sort((first, second) => first.localeCompare(second, undefined, { sensitivity: 'base' }));
    },
    getAdditionalWebsitesLinkLabel(websites: string[]): string {
      return `+${websites.length - 1}`;
    },
    removeAccountConfig(siteId: string|number, event: Event) {
      const siteInfos = this.sitesInfos as Record<string|number, Site>;
      this.removeAccountConfigName = siteInfos[siteId].name;

      Matomo.helper.modalConfirm(
        this.$refs.confirmRemoveAccountConfig as HTMLElement,
        {
          yes() {
            (event.target as HTMLFormElement).submit();
          },
        },
      );
    },
    removeAccount(account: YandexAccount, event: Event) {
      this.removeAccountName = account.name;

      Matomo.helper.modalConfirm(this.$refs.confirmDeleteAccount as HTMLElement, {
        yes() {
          (event.target as HTMLFormElement).submit();
        },
      });
    },
    removeClientConfig(event: Event) {
      Matomo.helper.modalConfirm(this.$refs.confirmRemoveClientConfig as HTMLElement, {
        yes() {
          (event.target as HTMLFormElement).submit();
        },
      });
    },
  },
  computed: {
    unverifiedWebsitesModalTitle() {
      return translate('SearchEngineKeywordsPerformance_UnverifiedWebsites');
    },
    showCredentialsError() {
      return !!this.userIsSuperUser && !!this.hasCredentialsError;
    },
    tokenAuth() {
      // read from the global Matomo already publishes on every page, the same way AjaxHelper does,
      // rather than rendering another copy of the token into the markup
      return Matomo.token_auth;
    },
    configuredMeasurablesToDisplay() {
      const entries = Object.entries(
        this.configuredMeasurables as Record<string | number, ConfiguredYandexInfo>,
      );

      return Object.fromEntries(
        entries.filter(([, config]) => {
          const [account] = config.yandexAccountAndHostId.split('##');
          return !!this.accounts[account];
        }).map(([siteId, config]) => {
          const [account, host] = config.yandexAccountAndHostId.split('##');
          const accountInfo = this.accounts[account] as YandexAccount;
          const { apiKey } = accountInfo;

          const hostUrlPair = Object.entries(accountInfo.urls).find(
            ([, data]) => data.host_id === host,
          );
          const hostUrl = hostUrlPair?.[0];

          return [
            siteId,
            {
              ...config,
              account,
              host,
              hostUrl,
              apiKeyDisplay: getDisplayApiKey(apiKey),
            },
          ];
        }),
      );
    },
    forwardToYandexAuthUrl() {
      return `?${MatomoUrl.stringify({
        ...MatomoUrl.urlParsed.value,
        action: 'forwardToYandexAuth',
      })}`;
    },
    visitOAuthHowTo() {
      const link = externalRawLink('https://matomo.org/faq/reports/import-yandex-search-keywords-into-matomo/');
      return translate(
        'SearchEngineKeywordsPerformance_VisitOAuthHowTo',
        `<a target="_blank" href="${link}" rel="noreferrer noopener">`,
        '</a>',
        'Yandex',
      );
    },
    accountsToDisplay() {
      const asArray = Object.entries(this.accounts);
      const filtered = asArray.filter(([, value]) => value.hasAccess);

      return Object.fromEntries(filtered);
    },
  },
});
